import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const solutionOptions = [
  { value: 'HR Management', label: 'HR Management' },
  { value: 'Project Management', label: 'Project Management' },
  { value: 'Sales Management', label: 'Sales Management' },
  { value: 'Marketing Management', label: 'Marketing Management' },
  { value: 'Finance Management', label: 'Finance Management' },
  { value: 'Custom IT Solutions', label: 'Custom IT Solutions' },
  { value: 'Multiple Solutions', label: 'Multiple Solutions' },
];

export const ContactForm = ({ 
  formType = 'General Inquiry', 
  showInterestedIn = true,
  showCompanySize = false,
  showTeamSize = false,
  showBudgetRange = false,
  showProjectRequirements = false,
  showSubject = false,
  showModulesInterested = false,
  interestedInDefault = '',
  className = ''
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    interested_in: interestedInDefault,
    message: '',
    company_size: '',
    team_size: '',
    budget_range: '',
    project_requirements: '',
    subject: '',
    modules_interested: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        form_type: formType,
      };

      const response = await axios.post(`${API}/contact`, payload);
      
      if (response.data.success) {
        toast.success('Thank you! Our team will reach out shortly.');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          interested_in: interestedInDefault,
          message: '',
          company_size: '',
          team_size: '',
          budget_range: '',
          project_requirements: '',
          subject: '',
          modules_interested: '',
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-6 ${className}`} data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-white">Name *</Label>
          <Input
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Your name"
            className="bg-secondary/50 border-border focus:border-primary h-12"
            data-testid="input-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company" className="text-white">Company *</Label>
          <Input
            id="company"
            name="company"
            type="text"
            required
            value={formData.company}
            onChange={handleChange}
            placeholder="Your company"
            className="bg-secondary/50 border-border focus:border-primary h-12"
            data-testid="input-company"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-white">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            className="bg-secondary/50 border-border focus:border-primary h-12"
            data-testid="input-email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-white">Phone *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91-78146-51194"
            className="bg-secondary/50 border-border focus:border-primary h-12"
            data-testid="input-phone"
          />
        </div>
      </div>

      {showInterestedIn && (
        <div className="space-y-2">
          <Label htmlFor="interested_in" className="text-white">Interested In *</Label>
          <Select
            value={formData.interested_in}
            onValueChange={(value) => handleSelectChange('interested_in', value)}
          >
            <SelectTrigger className="bg-secondary/50 border-border h-12" data-testid="select-interested-in">
              <SelectValue placeholder="Select a solution..." />
            </SelectTrigger>
            <SelectContent className="bg-secondary border-border">
              {solutionOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showSubject && (
        <div className="space-y-2">
          <Label htmlFor="subject" className="text-white">Subject</Label>
          <Input
            id="subject"
            name="subject"
            type="text"
            value={formData.subject}
            onChange={handleChange}
            placeholder="Subject of your inquiry"
            className="bg-secondary/50 border-border focus:border-primary h-12"
            data-testid="input-subject"
          />
        </div>
      )}

      {showCompanySize && (
        <div className="space-y-2">
          <Label htmlFor="company_size" className="text-white">Company Size *</Label>
          <Select
            value={formData.company_size}
            onValueChange={(value) => handleSelectChange('company_size', value)}
          >
            <SelectTrigger className="bg-secondary/50 border-border h-12" data-testid="select-company-size">
              <SelectValue placeholder="Select company size..." />
            </SelectTrigger>
            <SelectContent className="bg-secondary border-border">
              <SelectItem value="1-10 employees">1-10 employees</SelectItem>
              <SelectItem value="11-50 employees">11-50 employees</SelectItem>
              <SelectItem value="51-200 employees">51-200 employees</SelectItem>
              <SelectItem value="201-500 employees">201-500 employees</SelectItem>
              <SelectItem value="500+ employees">500+ employees</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {showTeamSize && (
        <div className="space-y-2">
          <Label htmlFor="team_size" className="text-white">Team Size *</Label>
          <Select
            value={formData.team_size}
            onValueChange={(value) => handleSelectChange('team_size', value)}
          >
            <SelectTrigger className="bg-secondary/50 border-border h-12" data-testid="select-team-size">
              <SelectValue placeholder="Select team size..." />
            </SelectTrigger>
            <SelectContent className="bg-secondary border-border">
              <SelectItem value="1-5 members">1-5 members</SelectItem>
              <SelectItem value="6-15 members">6-15 members</SelectItem>
              <SelectItem value="16-50 members">16-50 members</SelectItem>
              <SelectItem value="50+ members">50+ members</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {showBudgetRange && (
        <div className="space-y-2">
          <Label htmlFor="budget_range" className="text-white">Budget Range *</Label>
          <Select
            value={formData.budget_range}
            onValueChange={(value) => handleSelectChange('budget_range', value)}
          >
            <SelectTrigger className="bg-secondary/50 border-border h-12" data-testid="select-budget-range">
              <SelectValue placeholder="Select budget range..." />
            </SelectTrigger>
            <SelectContent className="bg-secondary border-border">
              <SelectItem value="$5,000 - $15,000">$5,000 - $15,000</SelectItem>
              <SelectItem value="$15,000 - $50,000">$15,000 - $50,000</SelectItem>
              <SelectItem value="$50,000 - $100,000">$50,000 - $100,000</SelectItem>
              <SelectItem value="$100,000+">$100,000+</SelectItem>
              <SelectItem value="Not sure yet">Not sure yet</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {showModulesInterested && (
        <div className="space-y-2">
          <Label htmlFor="modules_interested" className="text-white">Modules Interested</Label>
          <Input
            id="modules_interested"
            name="modules_interested"
            type="text"
            value={formData.modules_interested}
            onChange={handleChange}
            placeholder="e.g., HR, Project Management, Sales"
            className="bg-secondary/50 border-border focus:border-primary h-12"
            data-testid="input-modules-interested"
          />
        </div>
      )}

      {showProjectRequirements && (
        <div className="space-y-2">
          <Label htmlFor="project_requirements" className="text-white">Project Requirements *</Label>
          <Textarea
            id="project_requirements"
            name="project_requirements"
            required
            value={formData.project_requirements}
            onChange={handleChange}
            placeholder="Describe your project requirements..."
            className="bg-secondary/50 border-border focus:border-primary min-h-[120px]"
            data-testid="input-project-requirements"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="message" className="text-white">Message</Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us about your needs..."
          className="bg-secondary/50 border-border focus:border-primary min-h-[120px]"
          data-testid="input-message"
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-6 font-medium glow-primary glow-primary-hover btn-shine"
        data-testid="submit-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          'Request Callback'
        )}
      </Button>
    </form>
  );
};

export default ContactForm;
